"use client"

import * as React from "react"
import { formatDistanceStrict } from "date-fns"
import { Link } from "react-router-dom"
import type { LucideIcon } from "lucide-react"

import { Button, UserAvatar, cn, getUserInitials, identityInitialsTint } from "@ibada/ui"
import { DataTableVariant } from "@ibada/ui/components/data-table"

import { useLocale, useTranslations } from "@/i18n"
import {
  ProgressRowPill,
  ProgressRowPillTone,
  ProgressRowTone,
  progressRowTone,
} from "@/shared/components/progress-rows"
import { RowActionsMenu, type RowActionsMenuItem } from "@/shared/components/row-actions-menu"
import { getDateFnsLocale } from "@/shared/lib/dates/date-fns-locale"

/**
 * Alive cells: the progress rows' pieces (mark or avatar, pills, bar, money) as table cells, so a real column
 * table (header, sorting, column picker) reads like the pledge campaign board. Pair them with
 * `variant={DataTableVariant.Alive}` on the DataTable; accounting tables stay dense.
 */
export { DataTableVariant, ProgressRowPillTone as AlivePillTone, ProgressRowTone as AliveTone }
export { ProgressRowMark as AliveMark } from "@/shared/components/progress-rows"

const titleClassName = "truncate text-left text-[13.5px] font-extrabold text-foreground hover:underline focus-visible:underline focus-visible:outline-none"

/** Mark or avatar, a bold title that opens the record, a muted subtitle. */
export function AliveLeadCell({
  leading,
  title,
  subtitle,
  onOpen,
  href,
  className,
}: {
  leading?: React.ReactNode
  title: React.ReactNode
  subtitle?: React.ReactNode
  onOpen?: () => void
  href?: string
  className?: string
}) {
  return (
    <span className={cn("flex min-w-0 items-center gap-2.5", className)}>
      {leading}
      <span className='flex min-w-0 flex-col'>
        {href ? (
          <Link to={href} className={titleClassName}>
            {title}
          </Link>
        ) : onOpen ? (
          <button type='button' onClick={onOpen} className={titleClassName}>
            {title}
          </button>
        ) : (
          <span className='truncate text-[13.5px] font-extrabold text-foreground'>{title}</span>
        )}
        {subtitle ? <span className='text-muted-foreground flex min-w-0 items-center gap-1 truncate text-xs font-semibold'>{subtitle}</span> : null}
      </span>
    </span>
  )
}

/** Soft initials tint picked from the name; shared with the identity card (@ibada/ui) so colours match everywhere. */
export function aliveInitialsTint(name: string | null | undefined): string {
  return identityInitialsTint(name)
}

/** A person's photo, or their initials on a soft tint (instead of the same grey silhouette on every row). */
export function AliveAvatar({
  name,
  src,
  small = false,
  className,
  fallbackClassName,
}: {
  name: string | null | undefined
  src?: string | null
  /** The 24px avatar beside a name inside another column (leader, author). */
  small?: boolean
  className?: string
  fallbackClassName?: string
}) {
  return (
    <UserAvatar
      name={name}
      src={src ?? undefined}
      fallback={getUserInitials(name)}
      fallbackClassName={cn(small ? "text-[10px]" : "text-xs", "font-extrabold", aliveInitialsTint(name), fallbackClassName)}
      className={cn(small ? "size-6" : "size-8", "shrink-0", className)}
    />
  )
}

/** A record's initials on a soft tint, the lead mark for named things (groups, ministries). */
export function AliveInitialsMark({ name, className }: { name: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-extrabold",
        aliveInitialsTint(name),
        className,
      )}
    >
      {getUserInitials(name)}
    </span>
  )
}

/** A small avatar and the person's name; a muted dash when nobody is set. */
export function AlivePersonCell({
  name,
  avatarUrl,
  emptyLabel = "-",
}: {
  name: string | null | undefined
  avatarUrl?: string | null
  emptyLabel?: React.ReactNode
}) {
  if (!name) return <span className='text-muted-foreground text-sm'>{emptyLabel}</span>
  return (
    <span className='inline-flex min-w-0 items-center gap-2'>
      <AliveAvatar name={name} src={avatarUrl} small />
      <span className='truncate text-[13px] font-semibold'>{name}</span>
    </span>
  )
}

/** The status pill, by meaning (success, neutral, warning, danger). */
export function AliveStatusCell({ tone, children }: { tone: ProgressRowPillTone; children: React.ReactNode }) {
  return <ProgressRowPill tone={tone}>{children}</ProgressRowPill>
}

const BAR_CLASS: Record<ProgressRowTone, string> = {
  [ProgressRowTone.Default]: "bg-primary",
  [ProgressRowTone.Complete]: "bg-teal-600",
  [ProgressRowTone.Over]: "bg-destructive",
}

/**
 * "X sur Y" over a thin bar with its %. The label shows the real figure (125 %), the bar stops at full. Without
 * a percentage (nothing to measure against) only the value shows, with `children` as a muted hint.
 */
export function AliveProgressCell({
  value,
  target,
  percentage,
  tone,
  children,
}: {
  value: React.ReactNode
  target?: React.ReactNode
  percentage?: number | null
  tone?: ProgressRowTone
  children?: React.ReactNode
}) {
  const t = useTranslations("common.progressRow")
  const hasBar = percentage !== undefined && percentage !== null && !Number.isNaN(percentage)
  const resolvedTone = hasBar ? (tone ?? progressRowTone(percentage)) : ProgressRowTone.Default
  return (
    <span className='flex min-w-[10rem] flex-col gap-1'>
      <span className='flex items-baseline justify-between gap-2 text-[13px] tabular-nums'>
        <span className='truncate'>
          <span className='font-extrabold'>{value}</span>
          {target !== undefined && target !== null ? (
            <>
              {" "}
              <span className='text-muted-foreground'>{t("of", { amount: String(target) })}</span>
            </>
          ) : null}
        </span>
        {hasBar ? (
          <span
            data-tone={resolvedTone}
            className={cn("shrink-0 text-xs font-extrabold", resolvedTone === ProgressRowTone.Over && "text-destructive")}
          >
            {Math.round(percentage)} %
          </span>
        ) : null}
      </span>
      {hasBar ? (
        <span className='bg-muted h-1.5 w-full overflow-hidden rounded-full'>
          <span
            className={cn("block h-full rounded-full", BAR_CLASS[resolvedTone])}
            style={{ width: `${Math.max(0, Math.min(100, percentage))}%` }}
          />
        </span>
      ) : children ? (
        <span className='text-muted-foreground truncate text-xs font-semibold'>{children}</span>
      ) : null}
    </span>
  )
}

/** A count in bold with an optional muted hint beside it ("12 · dernière il y a 2 j"). */
export function AliveCountCell({ count, hint }: { count: React.ReactNode; hint?: React.ReactNode }) {
  return (
    <span className='inline-flex min-w-0 items-baseline gap-1.5 tabular-nums'>
      <span className='text-[13px] font-extrabold'>{count}</span>
      {hint ? <span className='text-muted-foreground truncate text-xs font-semibold'>{hint}</span> : null}
    </span>
  )
}

/** "il y a 3 jours", with the full date on hover. A muted dash when there is no date. */
export function AliveRelativeDateCell({
  value,
  emptyLabel = "-",
  now,
}: {
  value: string | Date | null | undefined
  emptyLabel?: React.ReactNode
  /** Tests pin the clock. */
  now?: Date
}) {
  const locale = useLocale()
  const date = value ? (value instanceof Date ? value : new Date(value)) : null
  if (!date || Number.isNaN(date.getTime())) return <span className='text-muted-foreground text-sm'>{emptyLabel}</span>
  const relative = formatAliveRelativeDate(date, locale, now)
  const full = new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: "numeric" }).format(date)
  return (
    <time dateTime={date.toISOString()} title={full} className='text-muted-foreground text-[13px] font-semibold'>
      {relative}
    </time>
  )
}

export function formatAliveRelativeDate(date: Date, locale: string, now?: Date): string {
  return formatDistanceStrict(date, now ?? new Date(), { addSuffix: true, locale: getDateFnsLocale(locale) })
}

/** One primary action shown as a small pill. */
export type AlivePrimaryAction = {
  label: string
  icon?: LucideIcon
  onSelect?: () => void
  href?: string
  disabled?: boolean
}

/**
 * The row's trailing cell: at most one primary action (a pill) and a ⋯ menu for the rest, instead of a row of
 * icon buttons. On a phone the menu becomes the app's bottom sheet (RowActionsMenu).
 */
export function AliveActionsCell({
  primary,
  groups,
  label,
  openLabel,
  sheetHeader,
}: {
  primary?: AlivePrimaryAction | null
  groups: RowActionsMenuItem[][]
  label: string
  openLabel: string
  sheetHeader: React.ReactNode
}) {
  const hasMenu = groups.some((group) => group.length > 0)
  const PrimaryIcon = primary?.icon
  return (
    <span className='flex items-center justify-end gap-1'>
      {primary ? (
        primary.href ? (
          <Button asChild variant='outline' size='sm' className='rounded-full'>
            <Link to={primary.href}>
              {PrimaryIcon ? <PrimaryIcon className='size-3.5' /> : null}
              {primary.label}
            </Link>
          </Button>
        ) : (
          <Button variant='outline' size='sm' className='rounded-full' disabled={primary.disabled} onClick={primary.onSelect}>
            {PrimaryIcon ? <PrimaryIcon className='size-3.5' /> : null}
            {primary.label}
          </Button>
        )
      ) : null}
      {hasMenu ? <RowActionsMenu label={label} openLabel={openLabel} sheetHeader={sheetHeader} groups={groups} /> : null}
    </span>
  )
}
